import api from '../../api/client';
import { Document, Page, Text, View, StyleSheet, Image, Font } from '@react-pdf/renderer';

Font.register({
    family: 'Courier',
    fonts: [
        { src: 'https://fonts.cdnfonts.com/s/8338/Courier.woff', fontWeight: 'normal' },
        { src: 'https://fonts.cdnfonts.com/s/8338/CourierBold.woff', fontWeight: 'bold' }
    ]
});

export const PDFContent = ({ id, transactionData, profile, stake, apiUrl }) => {
    const balance = transactionData?.totalCost - transactionData?.paidAmount;

    // console.log(profile, apiUrl);

    // Format currency values
    const formatCurrency = (value) => {
        if (typeof value !== 'number') value = Number(value) || 0;
        return value.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    };

    // Get current date and time
    const now = new Date();
    const dateStr = now.toLocaleDateString();
    const timeStr = now.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });

    // Helper function to determine item details
    const getItemDetails = (transactionData) => {
        if (transactionData?.birds) {
            return {
                name: 'Birds',
                quantity: transactionData.birds,
                unit: ''
            };
        }
        if (transactionData?.pigs) {
            return {
                name: 'Pigs',
                quantity: transactionData.pigs,
                unit: ''
            };
        }
        if (transactionData?.trays) {
            return {
                name: 'Egg Trays',
                quantity: transactionData.trays,
                unit: ''
            };
        }
        if (transactionData?.eggs) {
            return {
                name: 'Eggs',
                quantity: transactionData.eggs,
                unit: ''
            };
        }
        if (transactionData?.totalKgs) {
            return {
                name: 'Product',
                quantity: transactionData.totalKgs,
                unit: 'kg'
            };
        }
        if (transactionData?.sheep) {
            return {
                name: 'Sheep',
                quantity: transactionData.sheep,
                unit: ''
            };
        }
        if (transactionData?.animal) {
            return {
                name: 'Vaccination',
                quantity: transactionData.animal,
                unit: ''
            };
        }
        if (transactionData?.species) {
            return {
                name: 'Weaners',
                quantity: transactionData.tagNumber || 1,
                unit: ''
            };
        }
        // Default fallback
        return {
            name: 'Product',
            quantity: 1,
            unit: ''
        };
    };
    const itemDetails = getItemDetails(transactionData);
    const quantityDisplay = itemDetails.unit ? `${itemDetails.quantity} ${itemDetails.unit}` : itemDetails.quantity;

    return (
        <Document>
            <Page size={[226.77, 'auto']} style={styles.page}>
                {/* Store Header */}
                <View style={styles.storeHeader}>
                    {profile?.logo_base64 ? (
                        <Image src={profile.logo_base64} style={styles.logo} />
                    ) : (
                        <Text style={styles.storeName}>{profile?.name}</Text>
                    )}

                    <Text style={styles.storeAddress}>{profile?.address1 || null}</Text>
                    <Text style={styles.storeAddress}>{profile?.address2 || null}</Text>
                    <Text style={styles.storeContact}>
                        Tel: {profile?.phone1} {profile?.phone2 ? `| ${profile?.phone2}` : ''}
                    </Text>
                    <Text style={styles.storeContact}>Email: {profile?.email || 'info@sfarms.co.ke'}</Text>
                </View>

                {/* Receipt Metadata */}
                <View style={styles.metaSection}>
                    <Text style={styles.metaLabel}>
                        DATE: {dateStr} TIME: {timeStr}
                    </Text>
                    <Text style={styles.metaLabel}>TRX ID: {transactionData?.transactionId || '0000'}</Text>
                    <Text style={styles.metaLabel}>CASHIER: {transactionData?.servedBy || 'System'}</Text>
                </View>

                {/* Divider */}
                <Text style={styles.divider}>----------------------------------------------------</Text>

                {/* Customer Information */}
                <Text style={styles.customerLabel}>CUSTOMER:</Text>
                <Text style={styles.customerName}>
                    {stake?.find((o) => o.id === transactionData?.stakeholderId)?.name || 'WALK-IN CUSTOMER'}
                </Text>

                {/* Divider */}
                <Text style={styles.divider}>----------------------------------------------------</Text>

                {/* Items Table */}
                <View style={styles.table}>
                    <View style={styles.tableRow}>
                        <Text style={styles.itemHeader}>ITEM</Text>
                        <Text style={styles.qtyHeader}>QTY</Text>
                        <Text style={styles.priceHeader}>PRICE</Text>
                        <Text style={styles.totalHeader}>TOTAL</Text>
                    </View>

                    <View style={styles.tableRow}>
                        <Text style={styles.itemName}>{itemDetails.name}</Text>
                        <Text style={styles.itemQty}>{quantityDisplay}</Text>
                        <Text style={styles.itemPrice}>{formatCurrency(transactionData?.price)}</Text>
                        <Text style={styles.itemTotal}>{formatCurrency(transactionData?.totalCost || 0)}</Text>
                    </View>
                </View>

                {/* Divider */}
                <Text style={styles.divider}>----------------------------------------------------</Text>

                {/* Payment Summary */}
                <View style={styles.summarySection}>
                    <View style={styles.summaryRow}>
                        <Text style={styles.summaryLabel}>SUBTOTAL:</Text>
                        <Text style={styles.summaryValue}>{formatCurrency(transactionData?.totalCost)}</Text>
                    </View>
                    <View style={styles.summaryRow}>
                        <Text style={styles.summaryLabel}>PAID:</Text>
                        <Text style={styles.summaryValue}>{formatCurrency(transactionData?.paidAmount)}</Text>
                    </View>
                    <View style={styles.summaryRow}>
                        <Text style={[styles.summaryLabel, styles.balanceLabel]}>BALANCE:</Text>
                        <Text style={[styles.summaryValue, styles.balanceValue]}>{formatCurrency(balance)}</Text>
                    </View>
                </View>

                {/* Divider */}
                <Text style={styles.divider}>----------------------------------------------------</Text>

                {/* Payment Method */}
                <Text style={styles.paymentLabel}>PAYMENT: {transactionData?.paymentMethod || 'CASH'}</Text>
                <Text style={styles.paymentLabel}>REF: {transactionData?.pay_reference || 'N/A'}</Text>

                {/* Payment Status */}
                <Text style={styles.statusText}>
                    STATUS:{' '}
                    {transactionData?.paymentStatus === 'partially_paid'
                        ? 'PARTIAL PAYMENT'
                        : transactionData?.paymentStatus?.toUpperCase() || 'COMPLETE'}
                </Text>

                {/* Footer */}
                <View style={styles.footer}>
                    <Text style={styles.thankYou}>THANK YOU FOR YOUR BUSINESS!</Text>
                    <Text style={styles.footerText}>{profile?.tagline || 'Quality Poultry & Pig Products'}</Text>
                    <Text style={styles.footerNote}>{profile?.return_policy || 'Goods sold are not returnable'}</Text>
                </View>
            </Page>
        </Document>
    );
};

// Thermal receipt size: 80mm width (226.77 points)
const styles = StyleSheet.create({
    page: {
        padding: 10,
        fontFamily: 'Courier',
        fontSize: 8,
        width: 226.77 // Fixed 80mm width
    },
    storeHeader: {
        alignItems: 'center',
        marginBottom: 3
    },
    logo: {
        width: 120,
        height: 80,
        marginBottom: 10
    },

    storeName: {
        fontSize: 10,
        fontWeight: 'bold',
        textTransform: 'uppercase',
        marginBottom: 2,
        textAlign: 'center'
    },
    storeAddress: {
        fontSize: 7,
        textAlign: 'center',
        marginBottom: 1
    },
    storeContact: {
        fontSize: 7,
        textAlign: 'center',
        marginBottom: 1
    },
    metaSection: {
        marginVertical: 4
    },
    metaLabel: {
        fontSize: 7,
        marginBottom: 2,
        textAlign: 'center'
    },
    customerLabel: {
        fontSize: 8,
        fontWeight: 'bold',
        textAlign: 'center',
        marginBottom: 1
    },
    customerName: {
        fontSize: 8,
        fontWeight: 'bold',
        textAlign: 'center',
        textTransform: 'uppercase',
        marginBottom: 4
    },
    divider: {
        textAlign: 'center',
        fontSize: 7,
        marginVertical: 4
    },
    table: {
        marginVertical: 4
    },
    tableRow: {
        flexDirection: 'row',
        justifyContent: 'space-between',
        marginBottom: 3
    },
    itemHeader: {
        width: '40%',
        fontSize: 8,
        fontWeight: 'bold',
        textAlign: 'left'
    },
    qtyHeader: {
        width: '15%',
        fontSize: 8,
        fontWeight: 'bold',
        textAlign: 'right'
    },
    priceHeader: {
        width: '20%',
        fontSize: 8,
        fontWeight: 'bold',
        textAlign: 'right'
    },
    totalHeader: {
        width: '25%',
        fontSize: 8,
        fontWeight: 'bold',
        textAlign: 'right'
    },
    itemName: {
        width: '40%',
        fontSize: 8,
        textAlign: 'left',
        textTransform: 'uppercase'
    },
    itemQty: {
        width: '15%',
        fontSize: 8,
        textAlign: 'right'
    },
    itemPrice: {
        width: '20%',
        fontSize: 8,
        textAlign: 'right'
    },
    itemTotal: {
        width: '25%',
        fontSize: 8,
        fontWeight: 'bold',
        textAlign: 'right'
    },
    summarySection: {
        marginVertical: 4
    },
    summaryRow: {
        flexDirection: 'row',
        justifyContent: 'space-between',
        marginBottom: 3
    },
    summaryLabel: {
        fontSize: 8
    },
    summaryValue: {
        fontSize: 8,
        fontWeight: 'bold'
    },
    balanceLabel: {
        fontWeight: 'bold'
    },
    balanceValue: {
        fontWeight: 'bold',
        color: '#d9534f'
    },
    paymentLabel: {
        fontSize: 8,
        fontWeight: 'bold',
        marginBottom: 2
    },
    statusText: {
        fontSize: 8,
        fontWeight: 'bold',
        textAlign: 'center',
        marginVertical: 4,
        textTransform: 'uppercase',
        marginTop: 6
    },
    footer: {
        alignItems: 'center',
        marginTop: 8,
        marginBottom: 10
    },
    footerText: {
        fontSize: 7,
        fontWeight: 'bold',
        marginBottom: 2,
        textAlign: 'center'
    },
    thankYou: {
        fontSize: 8,
        fontWeight: 'bold',
        textTransform: 'uppercase',
        marginBottom: 3,
        textAlign: 'center'
    },
    footerNote: {
        fontSize: 6,
        textAlign: 'center',
        marginBottom: 1,
        fontStyle: 'italic'
    }
});

{
    /* Divider */
}
